/**
 * Logique d'affichage de l'écran Infoservice › Réglages : rien ici ne décide
 * de l'antenne (le plan vient du serveur), on met seulement en forme.
 */
import type { SlideshowConfig, SlideshowPlan, SlideshowTransition } from './infoserviceSlideshowApi.js'

/** Champs que la saisie modifie ; les fichiers sont enregistrés à part, tout de suite. */
export const EDITABLE_KEYS = [
  'mode', 'displaySec', 'holdThresholdSec', 'introTransition', 'announcementTransition',
  'musicEnabled', 'musicFadeInSec', 'musicFadeOutSec', 'cgHiddenDuringIntro',
] as const satisfies ReadonlyArray<keyof SlideshowConfig>

export type EditableConfig = Pick<SlideshowConfig, typeof EDITABLE_KEYS[number]>

export function editableOf(config: SlideshowConfig): EditableConfig {
  return Object.fromEntries(EDITABLE_KEYS.map(key => [key, config[key]])) as EditableConfig
}

export function sameEditable(a: EditableConfig, b: EditableConfig): boolean {
  return EDITABLE_KEYS.every(key => JSON.stringify(a[key]) === JSON.stringify(b[key]))
}

/** `m:ss`, ou `h:mm:ss` au-delà d'une heure. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = String(total % 60).padStart(2, '0')
  return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`
}

/** « 2 min 41 s », « 41 s », « 1 h 05 min ». */
export function formatSpan(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  if (total < 60) return `${total} s`
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  if (hours) return `${hours} h ${String(minutes).padStart(2, '0')} min`
  return seconds ? `${minutes} min ${seconds} s` : `${minutes} min`
}

/** Secondes avec deux décimales au plus, virgule française : `0,5 s`. */
export function formatSeconds(ms: number): string {
  return `${(Math.round(ms / 10) / 100).toLocaleString('fr-FR')} s`
}

/** Graduations de la frise : un pas lisible pour la durée, jamais plus de ~8. */
export function timelineTicks(durationMs: number): number[] {
  const steps = [10_000, 15_000, 30_000, 60_000, 120_000, 300_000, 600_000, 900_000, 1_800_000, 3_600_000]
  const step = steps.find(candidate => durationMs / candidate <= 8) ?? 3_600_000
  const ticks: number[] = []
  for (let at = 0; at <= durationMs; at += step) ticks.push(at)
  return ticks
}

/**
 * Étiquette courte de chaque annonce sur la frise : P1, P2… pour les
 * prioritaires, 1, 2, 3… pour les autres, dans l'ordre de première apparition.
 * Une même annonce garde son étiquette d'un tour à l'autre.
 */
export function stepLabels(plan: SlideshowPlan): Map<string, string> {
  const labels = new Map<string, string>()
  let priorities = 0
  let others = 0
  for (const step of plan.steps) {
    if (step.kind !== 'ANNOUNCEMENT' || labels.has(step.announcementId)) continue
    labels.set(step.announcementId, step.priority ? `P${++priorities}` : String(++others))
  }
  return labels
}

const CASPAR_LABELS: Record<string, string> = { CUT: 'Coupe', MIX: 'Fondu', WIPE: 'Volet', PUSH: 'Poussée', SLIDE: 'Glissé' }

export function transitionSummary(transition: SlideshowTransition): string {
  if (transition.kind === 'STINGER') return `Stinger, coupe à ${formatSeconds(transition.cutMs)}`
  if (transition.type === 'CUT' || transition.durationMs === 0) return 'Coupe franche'
  const direction = ['WIPE', 'PUSH', 'SLIDE'].includes(transition.type)
    ? (transition.direction === 'LEFT' ? ', droite → gauche' : ', gauche → droite')
    : ''
  return `${CASPAR_LABELS[transition.type]} ${formatSeconds(transition.durationMs)}${direction}`
}

export const CASPAR_TRANSITION_OPTIONS = [
  { value: 'MIX', label: 'Fondu' },
  { value: 'WIPE', label: 'Volet' },
  { value: 'PUSH', label: 'Poussée' },
  { value: 'SLIDE', label: 'Glissé' },
  { value: 'CUT', label: 'Coupe' },
] as const

/** Saisie en secondes (virgule ou point) vers millisecondes ; `null` si illisible. */
export function parseSecondsInput(value: string): number | null {
  const normalized = value.trim().replace(',', '.')
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null
  return Math.round(Number(normalized) * 1000)
}
